import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, 
    DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { ActiveAdSpaceInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getActiveAdSpacesColumns } from './ActiveAdSpacesColumns';
import { ActiveAdSpaceUpdateDialog } from './ActiveAdSpacesUpdateDialog';

const ActiveAdSpacesAdminList: React.FC = () => {
    const [adSpaces, setAdSpaces] = useState<ActiveAdSpaceInterface[]>([]);
    const [adSpace, setAdSpace] = useState<ActiveAdSpaceInterface | null>(null);
    const [ownerUserId, setOwnerUserId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [adSpaceId, setAdSpaceId] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [adSpaceToDelete, setAdSpaceToDelete] = useState<ActiveAdSpaceInterface | null>(null);
/*     const [toggleActiveDialogOpen, setToggleActiveDialogOpen] = useState(false);
    const [adSpaceToToggleActive, setAdSpaceToToggleActive] = useState<ActiveAdSpaceInterface | null>(null); */
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [adSpaceToUpdate, setAdSpaceToUpdate] = useState<ActiveAdSpaceInterface | null>(null);
    const [updateForm, setUpdateForm] = useState<Partial<ActiveAdSpaceInterface>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all ad spaces
    const handleGetAllAdSpaces = async () => {
        setError(null);
        setAdSpace(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<ActiveAdSpaceInterface[]>>('/activeAdSpaces');
            if (result.status && result.data) {
                setAdSpaces(result.data);
            } else {
                setError(result.message || 'Failed to fetch ad spaces');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch ad spaces');
        }
    };

    // Fetch ad space by id
    const handleGetAdSpaceById = async () => {
        setError(null);
        setAdSpaces([]);
        setSuccess(null);
        if (!adSpaceId) return setError('Please enter an AdSpace ID');
        try {
            const result = await apiGet<GenericResponseInterface<ActiveAdSpaceInterface>>(`/activeAdSpaces/${adSpaceId}`);
            if (result.status && result.data) {
                setAdSpace(result.data);
            } else {
                setError(result.message || 'Failed to fetch ad space');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch ad space');
        }
    };

    // Fetch ad spaces by owner user id
    const handleGetAdSpacesByOwnerUserId = async () => {
        setError(null);
        setAdSpace(null);
        setSuccess(null);
        if (!ownerUserId) return setError('Please enter an Owner User ID');
        try {
            const result = await apiGet<GenericResponseInterface<ActiveAdSpaceInterface[]>>(`/activeAdSpaces/userId/${ownerUserId}`);
            if (result.status && result.data) {
                setAdSpaces(result.data);
            } else {
                setError(result.message || 'Failed to fetch ad spaces for owner');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch ad spaces for owner');
        }
    };    

    // Delete ad space
    const handleDeleteAdSpace = async () => {
        if (!adSpaceToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/activeAdSpaces/delete/${adSpaceToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Ad space deleted successfully');
                setDeleteDialogOpen(false);
                setAdSpaceToDelete(null);
                handleGetAllAdSpaces();
            } else {
                setError(result.message || 'Failed to delete ad space');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete ad space');
        }
    };

    // Toggle active
/*     const handleToggleActive = async () => {
        if (!adSpaceToToggleActive) return;
        try {
            await apiRequest(`/activeAdSpaces/toggleActive/${adSpaceToToggleActive.id}`, 'PUT');
            setToggleActiveDialogOpen(false);
            setAdSpaceToToggleActive(null);
            handleGetAllAdSpaces();
        } catch (err: any) {
            setError(err.message || 'Failed to toggle active status');
        }
    }; */

    const handleUpdateAdSpace = async () => {
        if (!adSpaceToUpdate || !adSpaceToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<ActiveAdSpaceInterface>>(
                `/activeAdSpaces/update/${adSpaceToUpdate.id}`,
                'PUT',
                {
                    ...adSpaceToUpdate,
                    ...updateForm,
                }
            );
            if (result.status) {
                setSuccess(result.message || 'Ad space updated successfully');
                setUpdateDialogOpen(false);
                setAdSpaceToUpdate(null);
                setUpdateForm({});
                handleGetAllAdSpaces();
            } else {
                setUpdateError(result.message || 'Failed to update ad space');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update ad space');
        }
    };    

    const columns = getActiveAdSpacesColumns(
        setAdSpaceToDelete,
        setDeleteDialogOpen,
/*         setAdSpaceToToggleActive,
        setToggleActiveDialogOpen, */
        setAdSpaceToUpdate,
        setUpdateDialogOpen   
    );

    useEffect(() => {
        handleGetAllAdSpaces();
    }, []);

    useEffect(() => {
        if (updateDialogOpen && adSpaceToUpdate) {
            setUpdateForm({
                adSpaceId: adSpaceToUpdate.adSpaceId,
                adSpaceOwnerUserId: adSpaceToUpdate.adSpaceOwnerUserId,
                adSpaceRenterUserId: adSpaceToUpdate.adSpaceRenterUserId,
                adSpaceOwnerPackageId: adSpaceToUpdate.adSpaceOwnerPackageId,
                geoName: adSpaceToUpdate.geoName,
                totalAmountToBePaid: adSpaceToUpdate.totalAmountToBePaid,
                totalAmountPaidSoFar: adSpaceToUpdate.totalAmountPaidSoFar,
                totalAmountRemaining: adSpaceToUpdate.totalAmountRemaining,
                isFullyPaidToAdSpaceOwner: adSpaceToUpdate.isFullyPaidToAdSpaceOwner,
                weReceivedPayment: adSpaceToUpdate.weReceivedPayment,
                paymentTransactionId: adSpaceToUpdate.paymentTransactionId,
                isVerifiedAtHalfTime: adSpaceToUpdate.isVerifiedAtHalfTime,
                isVerifiedAtHalfTimeDate: adSpaceToUpdate.isVerifiedAtHalfTimeDate,
                isVerifiedAtFullTime: adSpaceToUpdate.isVerifiedAtFullTime,
                isVerifiedAtFullTimeDate: adSpaceToUpdate.isVerifiedAtFullTimeDate,
                waitingForApproval: adSpaceToUpdate.waitingForApproval,
                approvalDate: adSpaceToUpdate.approvalDate,
                waitingForPayment: adSpaceToUpdate.waitingForPayment,
                paymentDate: adSpaceToUpdate.paymentDate,
                active: adSpaceToUpdate.active,
                startDate: adSpaceToUpdate.startDate,
                endDate: adSpaceToUpdate.endDate,
            });
        }
    }, [updateDialogOpen, adSpaceToUpdate]);    

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllAdSpaces}>Get All Ad Spaces</Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAdSpaceById}>Get Ad Space by ID</Button>
                <TextField
                    label="AdSpace ID"
                    value={adSpaceId}
                    onChange={e => setAdSpaceId(e.target.value)}
                    size="small"
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAdSpacesByOwnerUserId}>Get Ad Spaces by Owner User ID</Button>
                <TextField
                    label="Owner User ID"
                    value={ownerUserId}
                    onChange={e => setOwnerUserId(e.target.value)}
                    size="small"
                />
            </Box>            
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {adSpaces.length > 0 && (
                    <DataGrid
                        rows={adSpaces.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {adSpace && (
                    <DataGrid
                        rows={[{ ...adSpace, id: adSpace.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this ad space?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteAdSpace}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Toggle active dialog */}
{/*             <Dialog open={toggleActiveDialogOpen} onClose={() => setToggleActiveDialogOpen(false)}>
                <DialogTitle>Toggle Active</DialogTitle>
                <DialogContent>
                    Are you sure you want to toggle the active status on this ad space?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setToggleActiveDialogOpen(false)}>Cancel</Button>
                    <Button color="warning" onClick={handleToggleActive}>Toggle</Button>
                </DialogActions>
            </Dialog>
 */}
            <ActiveAdSpaceUpdateDialog
                open={updateDialogOpen}
                adSpaceToUpdate={adSpaceToUpdate}
                setAdSpaceToUpdate={setAdSpaceToUpdate}
                updateForm={updateForm}
                setUpdateForm={setUpdateForm}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateAdSpace}
                updateError={updateError}
                updateSuccess={success}
            />

        </Box>
    );
};

export default ActiveAdSpacesAdminList;